import { motion, useReducedMotion } from 'motion/react';
import { Button } from './Button';
import { plausibleEventClass } from '../lib/plausible';

// Backgrounds:
//   md+    → players.png (the dark purple/black surface; the phone mockup
//            sits on top of it)
//   <md    → hero_full.png (full-bleed game-world scene; on mobile we drop
//            the standalone phone and just flood the photo behind the
//            heading + bottom panel)
const BG_DESKTOP = '/assets/images/backgrounds/players.png';
const BG_MOBILE = '/assets/images/backgrounds/hero_full.png';

// Placeholder for the standalone phone mockup. The designer doesn't have the
// final asset yet — using hero_full.png here gives the layout something to
// size and position against; swap the src when the real PNG lands.
const PHONE_PLACEHOLDER = '/assets/leaderboard/phone_game.png';

// Routes through /play — the smart gate decides registration vs weekly
// consent vs the game itself based on auth state.
const PLAY_NOW_TO = '/play';

/**
 * Play Along — second section of the /leaderboard page.
 *
 * Two responsive variants of the same content because the designer's mobile
 * and desktop intentions are genuinely different shapes:
 *
 *   md+ (tablet/desktop): dark `players.png` surface with a STANDALONE phone
 *     mockup floating above a red-gradient bottom panel. The panel carries
 *     the heading on the left + the prize-card + PLAY NOW on the right.
 *
 *   <md (mobile): no standalone phone — the full-bleed `hero_full.png`
 *     game-world scene IS the visual. The heading floats at the top of the
 *     section over a dark top-vignette; the bottom panel narrows to just
 *     the prize-card + PLAY NOW (no heading inside it).
 */
export default function PlayAlongSection() {
  const reduce = useReducedMotion();

  return (
    <motion.section
      aria-labelledby="play-along-heading"
      className="relative isolate flex min-h-[clamp(720px,100vh,1020px)] flex-col overflow-hidden px-4 pb-12 sm:px-12 sm:pb-14 lg:px-[50px] lg:pb-[50px]"
      initial={reduce ? false : { opacity: 0, y: 24 }}
      whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={reduce ? { duration: 0 } : { duration: 0.4, ease: 'easeOut' }}
    >
      <picture>
        <source media="(max-width: 767px)" srcSet={BG_MOBILE} />
        <img
          src={BG_DESKTOP}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-20 h-full w-full select-none object-cover"
        />
      </picture>

      {/*
        Mobile-only vignette overlays — darken the top and bottom of the
        game-world photo so the heading + bottom panel read cleanly. md+
        doesn't need this because the players.png surface is already dark
        and the panel sits on it with its own gradient.
      */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 md:hidden"
        style={{
          background: [
            'linear-gradient(180deg, rgb(23, 20, 29) 0%, rgba(47, 38, 81, 0) 35%)',
            'linear-gradient(180deg, rgba(47, 38, 81, 0) 60%, rgb(23, 20, 29) 100%)',
          ].join(', '),
        }}
      />

      {/*
        Mobile heading — pinned to the top of the section, over the photo.
        Hidden at md+ because the desktop/tablet variant places the heading
        inside the bottom panel.
      */}
      <h2
        id="play-along-heading"
        className="relative z-10 pt-6 font-[Orbitron] font-medium uppercase tracking-[-0.02em] leading-[1.15] text-white text-[clamp(20px,5vw,26px)] md:hidden"
      >
        <span className="text-[#ff0037]">Play @ Home</span>
        <br aria-hidden="true" />
        <span className="whitespace-nowrap">Jake&apos;s</span>
        <br aria-hidden="true" />
        <span className="whitespace-nowrap">Neighborhood</span>
        <br aria-hidden="true" />
        <span className="whitespace-nowrap">
          Swarm<span className="text-[#00ffb2]">_</span>
        </span>
      </h2>

      {/*
        Phone standalone — md+ only. Absolutely positioned so its bottom
        edge intentionally extends DOWN into the bottom panel's area; the
        panel's gradient is transparent at the top (rgba(255,0,81,0)) and
        only solid at the bottom (#ff0015), so the phone's bottom fades
        behind the red wash — matching Figma node 238:592.
        Aspect-[9/19] holds a phone-shaped rectangle so the placeholder
        (hero_full.png until the real phone PNG arrives) renders with the
        right silhouette; swap to object-contain when the real asset lands.
      */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-[8%] z-0 hidden aspect-[9/19] h-[81%] max-h-[840px] -translate-x-1/2 md:block"
      >
        <img
          src={PHONE_PLACEHOLDER}
          alt=""
          // Animated red halo per Figma node 238:592. The `ph-glow` keyframes
          // (defined in index.css) interpolate two layered drop-shadows in
          // lockstep so the glow breathes — ~2.6s ease-in-out, infinite. The
          // inline `style.filter` is a reduced-motion fallback: when the user
          // prefers no motion, we drop the animation class and the static
          // filter renders a steady glow instead of a pulsing one.
          className={`block h-full w-full select-none rounded-[40px] object-fill ${
            reduce ? '' : 'animate-[ph-glow_2.6s_ease-in-out_infinite]'
          }`}
          style={{
            filter:
              'drop-shadow(0 0 24px rgba(255, 0, 55, 0.75)) drop-shadow(0 0 60px rgba(255, 0, 55, 0.5))',
          }}
        />
      </div>

      {/* Bottom panel: red gradient with rounded-bottom corners. mt-auto
          pushes it to the bottom of the flex column at every breakpoint
          (the phone above rides absolutely, not in flow). z-10 ensures
          the panel paints over the phone's overflowing bottom edge. */}
      <div
        className="relative z-10 mt-auto mx-auto w-full max-w-[1280px] rounded-bl-[15px] rounded-br-[15px] px-4 pt-8 pb-6 sm:px-8 sm:pt-10 sm:pb-8 lg:px-[30px] lg:pt-[40px] lg:pb-[30px]"
        style={{
          background:
            'linear-gradient(to top, #ff0015 0%, rgba(255, 0, 81, 0.5) 25%, rgba(255, 0, 81, 0) 90%)',
        }}
      >
        <div className="flex flex-col items-stretch gap-6 md:flex-row md:items-end md:justify-between md:gap-10">
          {/* Desktop/tablet heading — placed inside the panel on the left. */}
          <h2
            aria-hidden="true"
            className="hidden font-[Orbitron] font-medium uppercase tracking-[-0.02em] leading-[1.1] text-white md:block md:text-[clamp(24px,3vw,32px)] md:max-w-[360px]"
          >
            <span className="text-[#ff0037]">Play @ Home</span>
            <br aria-hidden="true" />
            <span className="whitespace-nowrap">Jake&apos;s</span>
            <br aria-hidden="true" />
            <span className="whitespace-nowrap">Neighborhood</span>
            <br aria-hidden="true" />
            <span className="whitespace-nowrap">
              Swarm<span className="text-[#00ffb2]">_</span>
            </span>
          </h2>

          <div className="flex w-full flex-col gap-[10px] md:w-[300px]">
            <div
              className="flex items-end justify-between gap-3 rounded-[10px] p-[10px]"
              style={{
                background:
                  'linear-gradient(to bottom, rgba(61, 47, 69, 0) 0%, rgba(61, 47, 69, 0.4) 100%)',
              }}
            >
              <p className="font-[Orbitron] font-medium leading-snug text-white text-[clamp(14px,4.0vw,28px)] md:max-w-[143px] md:leading-[1.35] md:text-[clamp(12px,2.4vw,14px)]">
                Play for a chance to win a State Farm themed Secretlab TITAN Evo chair!
              </p>
              <picture className="block w-[40%] min-w-[120px] shrink-0 md:w-auto md:min-w-0">
                <source
                  media="(max-width: 640px)"
                  srcSet="/assets/images/secretlab/chair-double.webp"
                />
                <img
                  src="/assets/images/secretlab/chair-double.webp"
                  alt=""
                  aria-hidden="true"
                  width={480}
                  height={484}
                  className="pointer-events-none block h-auto w-full select-none drop-shadow-[0_12px_18px_rgba(0,0,0,0.45)] md:h-[clamp(140px,22vw,180px)] md:w-auto"
                />
              </picture>
            </div>

            <Button
              variant="primary"
              fullWidth
              to={PLAY_NOW_TO}
              className={plausibleEventClass('Play Click', { location: 'leaderboard' })}
            >
              Play Now
            </Button>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
